import React, { createContext, useContext, useEffect, useState } from 'react';

// Use global App Bridge from CDN
declare global {
  interface Window {
    shopify: any;
  }
}

interface ShopifyContextType {
  app: any;
  shop: string | null;
  host: string | null;
  getSessionToken: () => Promise<string>;
  isEmbedded: boolean;
}

const ShopifyContext = createContext<ShopifyContextType | null>(null);

export const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within ShopifyProvider');
  }
  return context;
};

interface ShopifyProviderProps {
  children: React.ReactNode;
}

export const ShopifyProvider: React.FC<ShopifyProviderProps> = ({ children }) => {
  const [app, setApp] = useState<any>(null);
  const [shop, setShop] = useState<string | null>(null);
  const [host, setHost] = useState<string | null>(null);
  const [isEmbedded, setIsEmbedded] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shopParam = urlParams.get('shop');
    const hostParam = urlParams.get('host');
    const embedded = urlParams.get('embedded') === '1' || window.top !== window.self;

    console.log('[Shopify] URL params:', { shopParam, hostParam, embedded });
    console.log('[Shopify] App Bridge available:', typeof window.shopify !== 'undefined');

    setShop(shopParam);
    setHost(hostParam);
    setIsEmbedded(embedded);

    // Wait for App Bridge to load from CDN
    const initializeAppBridge = async () => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds total
      
      while (attempts < maxAttempts) {
        if (typeof window.shopify !== 'undefined') {
          try {
            // New App Bridge doesn't require manual initialization
            // The shopify global variable is automatically available
            setApp(window.shopify);
            console.log('[Shopify] App Bridge initialized from official CDN for shop:', shopParam);
            return;
          } catch (error) {
            console.error('[Shopify] Error accessing App Bridge:', error);
            return;
          }
        }
    
        if (attempts % 10 === 0) {
          console.log(`[Shopify] Waiting for App Bridge CDN to load... attempt ${attempts + 1}`);
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      console.error('[Shopify] App Bridge official CDN failed to load after 5 seconds - this will fail Shopify checks');
    };
    
    initializeAppBridge();
  }, []);

  const fetchSessionToken = async (): Promise<string> => {
    if (!app) {
      throw new Error('App Bridge not initialized');
    }

    try {
      console.log('[Shopify] Getting session token from URL...');
      
      // Get JWT session token from URL (this is what Shopify provides for embedded apps)
      const urlParams = new URLSearchParams(window.location.search);
      const idToken = urlParams.get('id_token');
      const sessionParam = urlParams.get('session');
      
      console.log('[Shopify] URL parameters:', Object.fromEntries(urlParams.entries()));
      console.log('[Shopify] JWT id_token:', idToken);
      console.log('[Shopify] Session parameter:', sessionParam);
      
      if (idToken) {
        console.log('[Shopify] Found JWT id_token - using for authentication');
        return idToken;
      }
      
      if (sessionParam) {
        console.log('[Shopify] Fallback to session parameter');
        return sessionParam;
      }
      
      // If no session parameter, this is an error
      throw new Error('Session token not found in URL parameters - this should not happen in embedded apps');
      
    } catch (error) {
      console.error('[Shopify] Error getting session token:', error);
      throw error;
    }
  };

  const value: ShopifyContextType = {
    app,
    shop,
    host,
    getSessionToken: fetchSessionToken,
    isEmbedded,
  };

  return (
    <ShopifyContext.Provider value={value}>
      {children}
    </ShopifyContext.Provider>
  );
};

